@if ({view: currentView$ | async, selectedType: selectedType$ | async, loading: loading$ | async}; as state) {
  <div class="dialog-container">
    <!-- Selection View -->
    @if (state.view === DialogView.Selection) {
      <div class="content-area">
        <div class="title-section">
          <oph-x-button class="projects-dialog__close-button" (buttonClick)="onCancel()" />
          <h2 class="dialog-title">Select a Project Type</h2>
        </div>

        <div class="type-buttons-container">
          <button
            class="type-button"
            [class.selected]="state.selectedType === 'challenge'"
            (click)="onTypeSelect('challenge')"
          >
            <div class="type-icon-wrapper">
              <img src="assets/img/icons/challenge-builder/challenge.png" alt="Challenge" class="type-icon" />
            </div>
            <div class="type-text">
              <span class="type-label">Challenge</span>
              <span class="type-description">Work toward a goal on a cycle</span>
            </div>
          </button>

          <button
            class="type-button"
            [class.selected]="state.selectedType === 'project'"
            (click)="onTypeSelect('project')"
          >
            <div class="type-icon-wrapper">
              <img src="assets/img/icons/challenge-builder/project.png" alt="Project" class="type-icon" />
            </div>
            <div class="type-text">
              <span class="type-label">Project</span>
              <span class="type-description">Build a project with tasks</span>
            </div>
          </button>
        </div>
      </div>

      <div class="footer footer-centered">
        <oph-button appearance="outline" color="gray" (buttonClick)="onCancel()"> Cancel </oph-button>
      </div>
    }

    <!-- Challenge View -->
    @if (state.view === DialogView.Challenge) {
      <div class="content-area">
        <div class="title-section">
          <h2 class="dialog-title">Create new Challenge</h2>
        </div>

        <div class="challenge-content">
          <div class="fieldset">
            <h3 class="fieldset-title">Select a challenge cycle length</h3>
            <p class="fieldset-description">
              Challenge projects use repeating cycles to track progress. Choose 7 days if you'd like your Challenge
              tasks to repeat on a weekly schedule.
            </p>

            <div class="cycle-options">
              @for (option of cycleLengthOptions; track option) {
                <button
                  class="cycle-option"
                  [class.selected]="selectedCycleLength() === option"
                  (click)="onCycleLengthSelect(option)"
                >
                  {{ option }} days
                </button>
              }
            </div>
          </div>
        </div>
      </div>

      <div class="footer">
        <oph-button appearance="outline" color="gray" (buttonClick)="onCancel()"> Cancel </oph-button>
        <oph-button
          color="green"
          [loading]="state.loading"
          [style]="{width: '128px', minWidth: '128px'}"
          class="next-button"
          (buttonClick)="onNextChallenge()"
        >
          Next
          <mat-icon svgIcon="chevron-right"></mat-icon>
        </oph-button>
      </div>
    }

    <!-- Project View -->
    @if (state.view === DialogView.Project) {
      <div class="content-area">
        <div class="title-section">
          <h2 class="dialog-title">{{ isEditing() ? 'Edit Project' : 'Create new Project' }}</h2>
        </div>

        <div class="project-content">
          <div class="fieldset">
            <h3 class="fieldset-title">Set up your project</h3>
            <p class="fieldset-description">
              Add a title, color and date range for your new project. Ongoing projects will extend automatically.
            </p>
          </div>

          <!-- Color Grid -->
          <div class="color-grid-container">
            <div class="color-grid">
              @for (color of colors; track color) {
                <button
                  class="color-tile"
                  [style.background-color]="color"
                  [class.selected]="selectedColor() === color"
                  (click)="onColorSelect(color)"
                ></button>
              }
            </div>
          </div>

          <!-- Project Name Input -->
          <div class="project-name-container">
            <oph-input-inside-label
              label="Project name"
              inputType="text"
              [value]="projectName()"
              [placeholder]="'Project name'"
              [hideLabel]="true"
              [name]="'project-name'"
              [fontSize]="20"
              (valueChange)="onProjectNameChange($event)"
              [maxLength]="projectNameMaxLength()"
              [charCounterThreshold]="0"
            />
          </div>

          <!-- Date Selectors Row -->
          <div class="date-row">
            <div class="date-picker-wrapper">
              <button class="date-button" [matMenuTriggerFor]="startDateMenu" #startDateTrigger="matMenuTrigger">
                <span>{{ startDateDisplay() }}</span>
                <mat-icon svgIcon="calendar-filled"></mat-icon>
              </button>
              <mat-menu #startDateMenu="matMenu" panelClass="calendar-menu">
                <div class="calendar-menu-content" (click)="$event.stopPropagation()">
                  <mat-calendar
                    [selected]="startDate()"
                    [minDate]="startDateMin()"
                    [maxDate]="endDate()"
                    [headerComponent]="customHeader"
                    (selectedChange)="onStartDateChange($any({value: $event})); startDateTrigger.closeMenu()"
                  ></mat-calendar>
                </div>
              </mat-menu>
            </div>

            <span class="date-separator">to</span>

            <div class="date-picker-wrapper">
              <div class="end-date-group">
                <button class="date-button" [matMenuTriggerFor]="endDateMenu" #endDateTrigger="matMenuTrigger">
                  <span>{{ endDateDisplay() }}</span>
                  <mat-icon svgIcon="calendar-filled"></mat-icon>
                </button>
                @if (endDateSet()) {
                  <oph-x-button [width]="20" (buttonClick)="clearEndDate()"></oph-x-button>
                }
              </div>
              <mat-menu #endDateMenu="matMenu" panelClass="calendar-menu">
                <div class="calendar-menu-content" (click)="$event.stopPropagation()">
                  <mat-calendar
                    [selected]="endDate()"
                    [minDate]="startDate()"
                    [headerComponent]="customHeader"
                    (selectedChange)="onEndDateChange($any({value: $event})); endDateTrigger.closeMenu()"
                  ></mat-calendar>
                </div>
              </mat-menu>
            </div>

            <div class="draft-checkbox">
              <oph-checkbox
                [checked]="isDraft()"
                (valueChange)="onDraftChange($event)"
                text="Set to draft"
                textOrientation="right"
                color="green"
                [width]="25"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="footer">
        <oph-button appearance="outline" color="gray" (buttonClick)="onCancel()"> Cancel </oph-button>
        <oph-button
          class="next-button"
          color="green"
          [loading]="state.loading"
          [disabled]="!projectName()"
          [style]="{width: '128px', minWidth: '128px'}"
          (buttonClick)="onNextProject()"
        >
          {{ isEditing() ? 'Update' : 'Next' }}
          <mat-icon svgIcon="chevron-right"></mat-icon>
        </oph-button>
      </div>
    }
  </div>
}
